import { useQueries } from '@tanstack/react-query';
import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  Briefcase,
  Calendar,
  Coins,
  Download,
  Receipt,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { apiGet, getToken } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill } from '@/components/ui/Pill';
import { StatCard } from '@/components/ui/StatCard';
import { ErrorState, LoadingState } from '@/components/ui/States';
import { cn } from '@/utils/cls';
import { fmtRelative, inr, num } from '@/utils/format';

// ---------------------------------------------------------------------------
// Period model — covers everything an owner asks for ("show me last year",
// "this quarter vs same quarter last year", "the last 30 days", custom).
// ---------------------------------------------------------------------------

type Period =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_7_days'
  | 'last_30_days'
  | 'last_90_days'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'all_time'
  | 'custom';

const PERIODS: { id: Period; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this_week', label: 'This week' },
  { id: 'last_7_days', label: 'Last 7d' },
  { id: 'last_30_days', label: 'Last 30d' },
  { id: 'last_90_days', label: 'Last 90d' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'this_quarter', label: 'This quarter' },
  { id: 'last_quarter', label: 'Last quarter' },
  { id: 'this_year', label: 'This year' },
  { id: 'last_year', label: 'Last year' },
  { id: 'all_time', label: 'All time' },
  { id: 'custom', label: 'Custom…' },
];

// ---------------------------------------------------------------------------
// Response shapes — kept loose on purpose; the endpoint returns extras
// that other pages use (legacy shapes), so we type only what we render.
// ---------------------------------------------------------------------------

interface KpiBlock {
  revenue: number;
  net_revenue: number;
  tax: number;
  paid: number;
  due: number;
  bills: number;
  avg_bill: number;
  unique_customers: number;
  cogs: number;
  gross_profit: number;
  expenses: number;
  net_profit: number;
}

interface OverviewResp {
  period: Period;
  window: { start: string; end: string; days: number };
  current: KpiBlock;
  previous_year: KpiBlock;
  yoy: {
    revenue_pct: number | null;
    bills_pct: number | null;
    avg_bill_pct: number | null;
    customers_pct: number | null;
  };
  monthly: {
    month: string;
    revenue: number;
    paid: number;
    due: number;
    tax: number;
    bills: number;
  }[];
}

interface ProfitLossResp {
  revenue: number;
  tax: number;
  net_revenue: number;
  discount: number;
  cogs: number;
  gross_profit: number;
  gross_margin: number;
  expenses: number;
  expense_breakdown: { category: string; total: number }[];
  net_profit: number;
  net_margin: number;
}

interface PaymentMixResp {
  total: number;
  modes: { mode: string; total: number; count: number; pct: number }[];
}

interface CustomerInsightsResp {
  unique: number;
  new: number;
  returning: number;
  top: {
    customer_id: number;
    name: string;
    phone: string | null;
    bills: number;
    total: number;
    avg_bill: number;
    outstanding: number;
    last_at: string | null;
  }[];
  aging: { bucket: string; total: number; bills: number }[];
  churn_watch: {
    id: number;
    name: string;
    phone: string | null;
    total_purchases: number;
    outstanding: number;
    last_at: string | null;
    days_since: number | null;
  }[];
}

interface ProductInsightsResp {
  top: {
    product_id: number;
    name: string;
    sku: string | null;
    qty: number;
    revenue: number;
    cogs: number;
    profit: number;
    margin_pct: number;
  }[];
  slow: {
    product_id: number;
    name: string;
    sku: string | null;
    qty: number;
    revenue: number;
  }[];
  by_category: { category: string; revenue: number; qty: number }[];
}

interface EmployeeLeaderboardResp {
  rows: {
    user_id: number;
    name: string;
    revenue: number;
    paid: number;
    due: number;
    bills: number;
    avg_bill: number;
  }[];
}

interface HeatmapResp {
  days: number;
  cells: { dow: number; hour: number; revenue: number; bills: number }[];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const BUCKET_LABEL: Record<string, string> = {
  current: 'Within due date',
  '1_30': '1–30 days',
  '31_60': '31–60 days',
  '61_90': '61–90 days',
  '90_plus': '90+ days',
  no_due_date: 'No due date',
};

const PIE_COLORS = [
  'rgb(var(--brand-600))',
  'rgb(var(--brand-400))',
  'rgb(var(--brand-300))',
  'rgb(var(--brand-200))',
  '#94a3b8',
  '#f59e0b',
];

export function AnalyticsPage() {
  const [period, setPeriod] = useState<Period>('this_month');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');

  const params = useMemo(() => {
    if (period === 'custom' && from && to) return { period, from, to };
    return { period };
  }, [period, from, to]);

  const queries = useQueries({
    queries: [
      {
        queryKey: ['analytics', 'overview', params],
        queryFn: () => apiGet<OverviewResp>(endpoints.reportAnalyticsOverview, params),
      },
      {
        queryKey: ['analytics', 'pnl', params],
        queryFn: () => apiGet<ProfitLossResp>(endpoints.reportAnalyticsProfitLoss, params),
      },
      {
        queryKey: ['analytics', 'payment-mix', params],
        queryFn: () => apiGet<PaymentMixResp>(endpoints.reportAnalyticsPaymentMix, params),
      },
      {
        queryKey: ['analytics', 'customers', params],
        queryFn: () =>
          apiGet<CustomerInsightsResp>(endpoints.reportAnalyticsCustomers, params),
      },
      {
        queryKey: ['analytics', 'products', params],
        queryFn: () => apiGet<ProductInsightsResp>(endpoints.reportAnalyticsProducts, params),
      },
      {
        queryKey: ['analytics', 'employees', params],
        queryFn: () =>
          apiGet<EmployeeLeaderboardResp>(endpoints.reportAnalyticsEmployees, params),
      },
      {
        queryKey: ['analytics', 'heatmap'],
        queryFn: () => apiGet<HeatmapResp>(endpoints.reportAnalyticsHeatmap, { days: 90 }),
      },
    ],
  });

  const [overviewQ, pnlQ, payQ, custQ, prodQ, empQ, heatQ] = queries;

  const anyLoading = queries.some((q) => q.isLoading);
  const overviewErr = overviewQ.isError;

  if (anyLoading && !overviewQ.data) return <LoadingState />;
  if (overviewErr || !overviewQ.data) {
    return (
      <ErrorState
        title="Couldn't load analytics"
        description={(overviewQ.error as Error)?.message}
        onRetry={() => overviewQ.refetch()}
      />
    );
  }

  const overview = overviewQ.data;
  const pnl = pnlQ.data;
  const pay = payQ.data;
  const cust = custQ.data;
  const prod = prodQ.data;
  const emp = empQ.data;
  const heat = heatQ.data;

  return (
    <>
      <PageHeader
        title="Analytics"
        description="Every number you might want to know — your shop, top to bottom."
        actions={<ExportMenu period={period} from={from} to={to} />}
      />

      {/* Period picker */}
      <Card className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={cn(
                'h-9 px-3.5 rounded-full text-sm font-medium transition-colors',
                period === p.id
                  ? 'bg-brand-600 text-white'
                  : 'bg-surface border border-line text-ink-soft hover:text-ink hover:bg-surface-2',
              )}
            >
              {p.label}
            </button>
          ))}
          {period === 'custom' && (
            <div className="ml-2 flex items-center gap-2">
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
                className="h-9 rounded-lg border border-line bg-surface px-2.5 text-sm"
              />
              <span className="text-ink-soft text-sm">→</span>
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
                className="h-9 rounded-lg border border-line bg-surface px-2.5 text-sm"
              />
            </div>
          )}
          <span className="ml-auto text-xs text-ink-soft inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            {fmtRelative(overview.window.start)} → {fmtRelative(overview.window.end)} ·
            {' '}{overview.window.days}d
          </span>
        </div>
      </Card>

      {/* KPIs with YoY */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-5">
        <KpiTile
          label="Revenue"
          value={<Money value={overview.current.revenue} />}
          delta={overview.yoy.revenue_pct}
          icon={<TrendingUp className="h-5 w-5" />}
          subtext={`vs ${inr(overview.previous_year.revenue)} last yr`}
        />
        <KpiTile
          label="Bills"
          value={num(overview.current.bills)}
          delta={overview.yoy.bills_pct}
          icon={<Receipt className="h-5 w-5" />}
          subtext={`Avg ${inr(overview.current.avg_bill)} / bill`}
        />
        <KpiTile
          label="Customers"
          value={num(overview.current.unique_customers)}
          delta={overview.yoy.customers_pct}
          icon={<Users className="h-5 w-5" />}
          subtext={`${cust?.new ?? 0} new · ${cust?.returning ?? 0} repeat`}
        />
        <KpiTile
          label="Gross profit"
          value={<Money value={overview.current.gross_profit} />}
          icon={<Coins className="h-5 w-5" />}
          subtext={`Net ${inr(overview.current.net_profit)} after expenses`}
        />
      </div>

      {/* Monthly revenue + bills trend (13 months) */}
      <Card className="mb-5">
        <SectionHeader
          title="Last 13 months"
          description="Revenue and bills, side by side. Hover any month for the exact numbers."
        />
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={overview.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--line))" />
              <XAxis
                dataKey="month"
                stroke="rgb(var(--ink-soft))"
                fontSize={11}
                tickFormatter={(m: string) => fmtMonth(m)}
              />
              <YAxis
                yAxisId="left"
                stroke="rgb(var(--ink-soft))"
                fontSize={11}
                width={50}
                tickFormatter={(v) => `${Math.round(v / 1000)}k`}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="rgb(var(--ink-soft))"
                fontSize={11}
                width={40}
              />
              <Tooltip
                contentStyle={{
                  background: 'rgb(var(--surface))',
                  border: '1px solid rgb(var(--line))',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value: number, name: string) =>
                  name === 'Bills' ? num(value) : inr(value)
                }
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                yAxisId="left"
                dataKey="revenue"
                name="Revenue"
                fill="rgb(var(--brand-500))"
                radius={[6, 6, 0, 0]}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="bills"
                name="Bills"
                stroke="rgb(var(--brand-700))"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* P&L + payment mix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        {pnl && (
          <Card>
            <SectionHeader
              title="Profit & loss"
              description="Where every rupee from this period went."
              actions={
                <Pill tone={pnl.net_profit >= 0 ? 'success' : 'danger'}>
                  {pnl.net_margin}% net margin
                </Pill>
              }
            />
            <PnlBreakdown pnl={pnl} />
          </Card>
        )}
        {pay && pay.modes.length > 0 && (
          <Card>
            <SectionHeader
              title="How customers paid"
              description={`${inr(pay.total)} collected across ${pay.modes.length} channels.`}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pay.modes}
                      dataKey="total"
                      nameKey="mode"
                      cx="50%"
                      cy="50%"
                      innerRadius={48}
                      outerRadius={78}
                      paddingAngle={2}
                    >
                      {pay.modes.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: 'rgb(var(--surface))',
                        border: '1px solid rgb(var(--line))',
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(v: number) => inr(v)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="text-sm space-y-2">
                {pay.modes.map((m, i) => (
                  <li key={m.mode} className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                      />
                      <span className="capitalize">{m.mode}</span>
                    </span>
                    <span className="tabular-nums text-ink-soft">
                      {m.pct}% · <Money value={m.total} className="text-ink" />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        )}
      </div>

      {/* Customers — new vs returning + outstanding aging */}
      {cust && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Card>
            <SectionHeader
              title="New vs returning"
              description="A healthy shop has both — and watches the ratio."
            />
            <CustomerSplit
              uniq={cust.unique}
              newCount={cust.new}
              returning={cust.returning}
            />
          </Card>
          <Card>
            <SectionHeader
              title="Outstanding (udhaari) aging"
              description="How old is your unpaid receivable?"
            />
            <AgingChart aging={cust.aging} />
          </Card>
        </div>
      )}

      {/* Top customers + Churn watch */}
      {cust && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Card padded={false}>
            <div className="px-5 py-4 border-b border-line">
              <h2 className="text-base font-semibold">Top customers in this period</h2>
            </div>
            {cust.top.length === 0 ? (
              <p className="px-5 py-8 text-sm text-ink-soft text-center">
                No customer-tagged bills in this window.
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {cust.top.slice(0, 8).map((c) => (
                  <li
                    key={c.customer_id}
                    className="flex items-center justify-between px-5 py-2.5 gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.name}</div>
                      <div className="text-xs text-ink-soft">
                        {c.bills} bills · avg {inr(c.avg_bill)}
                        {c.outstanding > 0 && (
                          <> · <span className="text-warn">due {inr(c.outstanding)}</span></>
                        )}
                      </div>
                    </div>
                    <Money value={c.total} className="text-sm" />
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card padded={false}>
            <div className="px-5 py-4 border-b border-line flex items-center gap-2">
              <h2 className="text-base font-semibold flex-1">Lapsed customers</h2>
              <Pill tone="warn">90+ days quiet</Pill>
            </div>
            {cust.churn_watch.length === 0 ? (
              <p className="px-5 py-8 text-sm text-ink-soft text-center">
                Nice — every paying customer has come back recently.
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {cust.churn_watch.slice(0, 8).map((c) => (
                  <li
                    key={c.id}
                    className="flex items-center justify-between px-5 py-2.5 gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.name}</div>
                      <div className="text-xs text-ink-soft">
                        Last: {fmtRelative(c.last_at)} · lifetime {inr(c.total_purchases)}
                      </div>
                    </div>
                    {c.days_since != null && (
                      <Pill tone="neutral">{c.days_since}d</Pill>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {/* Products: top sellers with margin + slow movers + category mix */}
      {prod && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Card padded={false}>
            <div className="px-5 py-4 border-b border-line">
              <h2 className="text-base font-semibold">Top sellers (with margin)</h2>
            </div>
            {prod.top.length === 0 ? (
              <p className="px-5 py-8 text-sm text-ink-soft text-center">
                No sales in this window.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-ink-soft">
                    <tr>
                      <th className="text-left px-5 py-2">Product</th>
                      <th className="text-right px-2 py-2">Qty</th>
                      <th className="text-right px-2 py-2">Revenue</th>
                      <th className="text-right px-5 py-2">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prod.top.slice(0, 10).map((p) => (
                      <tr key={p.product_id} className="border-t border-line">
                        <td className="px-5 py-2">
                          <div className="font-medium truncate max-w-[14rem]">{p.name}</div>
                          {p.sku && <div className="text-xs text-ink-soft">{p.sku}</div>}
                        </td>
                        <td className="text-right px-2 py-2 tabular-nums">{num(p.qty)}</td>
                        <td className="text-right px-2 py-2"><Money value={p.revenue} /></td>
                        <td className="text-right px-5 py-2 tabular-nums">
                          <span className={p.margin_pct >= 20 ? 'text-success' : p.margin_pct >= 0 ? 'text-ink' : 'text-danger'}>
                            {p.margin_pct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card>
            <SectionHeader
              title="Revenue by category"
              description="Where the rupees are coming from."
            />
            {prod.by_category.length === 0 ? (
              <p className="text-sm text-ink-soft py-12 text-center">No data yet.</p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={prod.by_category} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--line))" />
                    <XAxis
                      type="number"
                      stroke="rgb(var(--ink-soft))"
                      fontSize={11}
                      tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                    />
                    <YAxis
                      type="category"
                      dataKey="category"
                      stroke="rgb(var(--ink-soft))"
                      fontSize={11}
                      width={110}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'rgb(var(--surface))',
                        border: '1px solid rgb(var(--line))',
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(v: number) => inr(v)}
                    />
                    <Bar
                      dataKey="revenue"
                      fill="rgb(var(--brand-500))"
                      radius={[0, 6, 6, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Slow movers + Employee leaderboard */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        {prod && (
          <Card padded={false}>
            <div className="px-5 py-4 border-b border-line flex items-center gap-2">
              <h2 className="text-base font-semibold flex-1">Slow movers</h2>
              <Pill tone="warn">consider clearance</Pill>
            </div>
            {prod.slow.length === 0 ? (
              <p className="px-5 py-8 text-sm text-ink-soft text-center">
                Everything is moving — nice.
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {prod.slow.slice(0, 8).map((p) => (
                  <li key={p.product_id} className="flex items-center justify-between px-5 py-2.5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      {p.sku && <div className="text-xs text-ink-soft">{p.sku}</div>}
                    </div>
                    <div className="text-right">
                      <div className="text-sm tabular-nums">{num(p.qty)} units</div>
                      <div className="text-xs text-ink-soft">{inr(p.revenue)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {emp && (
          <Card padded={false}>
            <div className="px-5 py-4 border-b border-line flex items-center gap-2">
              <h2 className="text-base font-semibold flex-1">Employee leaderboard</h2>
              <Briefcase className="h-4 w-4 text-ink-soft" />
            </div>
            {emp.rows.length === 0 ? (
              <p className="px-5 py-8 text-sm text-ink-soft text-center">
                No bills tagged with an employee in this window.
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {emp.rows.slice(0, 8).map((e, i) => (
                  <li key={e.user_id} className="flex items-center justify-between px-5 py-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-7 w-7 rounded-full bg-brand-500/10 text-brand-700 grid place-items-center text-xs font-semibold">
                        {i + 1}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{e.name}</div>
                        <div className="text-xs text-ink-soft">
                          {e.bills} bills · avg {inr(e.avg_bill)}
                          {e.due > 0 && <> · due {inr(e.due)}</>}
                        </div>
                      </div>
                    </div>
                    <Money value={e.revenue} className="text-sm" />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
      </div>

      {/* Heatmap */}
      {heat && heat.cells.length > 0 && (
        <Card className="mb-5">
          <SectionHeader
            title="When customers buy"
            description={`Revenue heatmap, last ${heat.days} days. Darker = busier.`}
          />
          <Heatmap cells={heat.cells} />
        </Card>
      )}

      {/* Tax + due trend */}
      <Card>
        <SectionHeader
          title="Paid vs due (last 13 months)"
          description="How quickly does revenue actually become cash?"
        />
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={overview.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--line))" />
              <XAxis
                dataKey="month"
                stroke="rgb(var(--ink-soft))"
                fontSize={11}
                tickFormatter={(m: string) => fmtMonth(m)}
              />
              <YAxis
                stroke="rgb(var(--ink-soft))"
                fontSize={11}
                width={50}
                tickFormatter={(v) => `${Math.round(v / 1000)}k`}
              />
              <Tooltip
                contentStyle={{
                  background: 'rgb(var(--surface))',
                  border: '1px solid rgb(var(--line))',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v: number) => inr(v)}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="paid"
                name="Paid"
                stackId="1"
                stroke="rgb(var(--brand-600))"
                fill="rgb(var(--brand-500) / 0.55)"
              />
              <Area
                type="monotone"
                dataKey="due"
                name="Due"
                stackId="1"
                stroke="#f59e0b"
                fill="#f59e0b66"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiTile({
  label,
  value,
  delta,
  icon,
  subtext,
}: {
  label: string;
  value: React.ReactNode;
  delta?: number | null;
  icon: React.ReactNode;
  subtext?: string;
}) {
  let deltaTone: 'success' | 'warn' | 'danger' | 'neutral' = 'neutral';
  let deltaNode: React.ReactNode = subtext ?? null;
  if (typeof delta === 'number') {
    deltaTone = delta > 0 ? 'success' : delta < 0 ? 'danger' : 'neutral';
    deltaNode = (
      <span className="inline-flex items-center gap-1">
        {delta > 0 ? (
          <ArrowUpRight className="h-3 w-3" />
        ) : delta < 0 ? (
          <ArrowDownRight className="h-3 w-3" />
        ) : null}
        {delta > 0 ? '+' : ''}
        {delta}% YoY
        {subtext && <span className="text-ink-soft"> · {subtext}</span>}
      </span>
    );
  } else if (delta === null) {
    deltaNode = (
      <span className="inline-flex items-center gap-1 text-ink-soft">
        <Activity className="h-3 w-3" /> new this year
        {subtext && <span> · {subtext}</span>}
      </span>
    );
  }
  return (
    <StatCard
      label={label}
      value={value}
      icon={icon}
      delta={deltaNode}
      deltaTone={deltaTone}
    />
  );
}

function PnlBreakdown({ pnl }: { pnl: ProfitLossResp }) {
  const rows: { label: string; value: number; tone?: 'pos' | 'neg' | 'neutral' }[] = [
    { label: 'Revenue (gross)', value: pnl.revenue, tone: 'pos' },
    { label: '− Tax collected', value: -pnl.tax, tone: 'neutral' },
    { label: 'Net revenue', value: pnl.net_revenue, tone: 'pos' },
    { label: '− Cost of goods sold', value: -pnl.cogs, tone: 'neg' },
    { label: 'Gross profit', value: pnl.gross_profit, tone: pnl.gross_profit >= 0 ? 'pos' : 'neg' },
    { label: '− Expenses', value: -pnl.expenses, tone: 'neg' },
    { label: 'Net profit', value: pnl.net_profit, tone: pnl.net_profit >= 0 ? 'pos' : 'neg' },
  ];

  return (
    <div>
      <ul className="divide-y divide-line text-sm">
        {rows.map((r, i) => (
          <li
            key={i}
            className={cn(
              'flex items-center justify-between py-2',
              (r.label.startsWith('Net') || r.label.startsWith('Gross profit')) &&
                'font-semibold',
            )}
          >
            <span>{r.label}</span>
            <Money
              value={Math.abs(r.value)}
              tone={r.tone === 'pos' ? 'positive' : r.tone === 'neg' ? 'negative' : 'auto'}
            />
          </li>
        ))}
      </ul>
      {pnl.expense_breakdown.length > 0 && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-ink-soft hover:text-ink">
            Expenses by category ({pnl.expense_breakdown.length})
          </summary>
          <ul className="mt-2 space-y-1.5">
            {pnl.expense_breakdown.map((e) => (
              <li key={e.category} className="flex items-center justify-between">
                <span className="capitalize">{e.category}</span>
                <Money value={e.total} />
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function CustomerSplit({
  uniq,
  newCount,
  returning,
}: {
  uniq: number;
  newCount: number;
  returning: number;
}) {
  const total = newCount + returning || 1;
  const newPct = Math.round((newCount / total) * 100);
  const repPct = 100 - newPct;
  return (
    <div>
      <div className="flex items-end justify-between mb-2">
        <div>
          <div className="stat-value">{num(uniq)}</div>
          <div className="text-xs text-ink-soft">unique customers</div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Pill tone="success">{newPct}% new</Pill>
          <Pill tone="neutral">{repPct}% returning</Pill>
        </div>
      </div>
      <div className="flex h-3 rounded-full overflow-hidden bg-surface-2">
        <div
          className="bg-brand-500"
          style={{ width: `${newPct}%` }}
          title={`New: ${newCount}`}
        />
        <div
          className="bg-brand-300"
          style={{ width: `${repPct}%` }}
          title={`Returning: ${returning}`}
        />
      </div>
      <p className="text-xs text-ink-soft mt-3">
        New: {num(newCount)} · Returning: {num(returning)}
      </p>
    </div>
  );
}

function AgingChart({
  aging,
}: {
  aging: { bucket: string; total: number; bills: number }[];
}) {
  const order = ['current', '1_30', '31_60', '61_90', '90_plus', 'no_due_date'];
  const data = order
    .map((b) => aging.find((a) => a.bucket === b))
    .filter(Boolean)
    .map((a) => ({
      bucket: BUCKET_LABEL[a!.bucket] || a!.bucket,
      total: a!.total,
      bills: a!.bills,
    }));
  if (data.length === 0) {
    return (
      <p className="text-sm text-ink-soft py-12 text-center">
        Nothing outstanding — every bill is settled.
      </p>
    );
  }
  return (
    <div className="h-52">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--line))" />
          <XAxis dataKey="bucket" stroke="rgb(var(--ink-soft))" fontSize={11} />
          <YAxis
            stroke="rgb(var(--ink-soft))"
            fontSize={11}
            width={50}
            tickFormatter={(v) => `${Math.round(v / 1000)}k`}
          />
          <Tooltip
            contentStyle={{
              background: 'rgb(var(--surface))',
              border: '1px solid rgb(var(--line))',
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(v: number, _n, p) =>
              [inr(v), `${p.payload.bills} bills`]
            }
          />
          <Bar dataKey="total" radius={[6, 6, 0, 0]}>
            {data.map((d, i) => (
              <Cell
                key={i}
                fill={
                  d.bucket.includes('90') ? '#dc2626'
                  : d.bucket.includes('61') ? '#f97316'
                  : d.bucket.includes('31') ? '#f59e0b'
                  : d.bucket.includes('1') ? '#fbbf24'
                  : 'rgb(var(--brand-400))'
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Heatmap({ cells }: { cells: HeatmapResp['cells'] }) {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const max = Math.max(1, ...cells.map((c) => c.revenue));
  // Build a lookup
  const map = new Map<string, { revenue: number; bills: number }>();
  for (const c of cells) map.set(`${c.dow}-${c.hour}`, { revenue: c.revenue, bills: c.bills });

  return (
    <div className="overflow-x-auto">
      <div className="grid" style={{ gridTemplateColumns: '40px repeat(24, minmax(18px, 1fr))', gap: 2, minWidth: 600 }}>
        <div />
        {Array.from({ length: 24 }).map((_, h) => (
          <div key={h} className="text-[10px] text-ink-soft text-center">
            {h % 3 === 0 ? (h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`) : ''}
          </div>
        ))}
        {days.map((d, dow) => (
          <>
            <div key={d} className="text-xs text-ink-soft pr-1 leading-[18px]">{d}</div>
            {Array.from({ length: 24 }).map((_, h) => {
              const cell = map.get(`${dow}-${h}`);
              const intensity = cell ? cell.revenue / max : 0;
              const bg = intensity === 0
                ? 'rgb(var(--surface-2))'
                : `rgba(99, 102, 241, ${0.15 + intensity * 0.85})`;
              return (
                <div
                  key={`${dow}-${h}`}
                  title={cell ? `${d} ${h}:00 · ${inr(cell.revenue)} · ${cell.bills} bills` : `${d} ${h}:00`}
                  className="h-[18px] rounded-[3px]"
                  style={{ background: bg }}
                />
              );
            })}
          </>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 mt-3 text-xs text-ink-soft">
        <span>Quiet</span>
        <div className="flex">
          {[0.15, 0.35, 0.55, 0.75, 0.95].map((a) => (
            <div
              key={a}
              className="h-3 w-4"
              style={{ background: `rgba(99, 102, 241, ${a})` }}
            />
          ))}
        </div>
        <span>Busy</span>
      </div>
    </div>
  );
}

function ExportMenu({
  period,
  from,
  to,
}: {
  period: Period;
  from: string;
  to: string;
}) {
  const open = (type: 'bills' | 'payments' | 'expenses' | 'customers' | 'products') => {
    const token = getToken() ?? '';
    const qs = new URLSearchParams({ type, period, token });
    if (period === 'custom') {
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
    }
    window.open(`${endpoints.reportExport}?${qs.toString()}`, '_blank', 'noopener');
  };
  return (
    <div className="relative group">
      <Button variant="outline" iconLeft={<Download className="h-4 w-4" />}>
        Export CSV
      </Button>
      <div className="absolute right-0 top-full mt-1 z-30 hidden group-hover:block group-focus-within:block">
        <div className="card p-1.5 w-44 shadow-lg">
          {[
            { id: 'bills', label: 'Bills', Icon: Receipt },
            { id: 'payments', label: 'Payments', Icon: Wallet },
            { id: 'expenses', label: 'Expenses', Icon: Coins },
            { id: 'customers', label: 'Customers', Icon: Users },
            { id: 'products', label: 'Products', Icon: TrendingUp },
          ].map((o) => (
            <button
              key={o.id}
              onClick={() => open(o.id as any)}
              className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-surface-2 inline-flex items-center gap-2"
            >
              <o.Icon className="h-4 w-4 text-ink-soft" />
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function fmtMonth(m: string): string {
  // m comes in as YYYY-MM. Render as "Apr '26".
  const [y, mo] = m.split('-');
  if (!y || !mo) return m;
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
}
